export interface CartLine {
  productId: string;
  variantId: number;
  title: string;
  variantLabel: string;
  image: string;
  unitPrice: number; // dollars, display snapshot (re-priced server-side at checkout)
  qty: number;
}

export const CART_KEY = "oddstack_cart_v1";

const listeners = new Set<() => void>();
let cache: CartLine[] | null = null;

function hasStorage(): boolean {
  return typeof window !== "undefined" && !!window.localStorage;
}

function read(): CartLine[] {
  if (cache) return cache;
  if (!hasStorage()) return (cache = []);
  try {
    const raw = window.localStorage.getItem(CART_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    cache = Array.isArray(parsed) ? parsed : [];
  } catch {
    cache = [];
  }
  return cache;
}

function write(lines: CartLine[]): void {
  cache = lines;
  if (hasStorage()) {
    try { window.localStorage.setItem(CART_KEY, JSON.stringify(lines)); } catch { /* quota */ }
  }
  listeners.forEach((l) => l());
}

const clampQty = (n: number): number =>
  Number.isFinite(n) ? Math.max(1, Math.min(99, Math.round(n))) : 1;

export function getCart(): CartLine[] {
  return read();
}

export function addToCart(line: Omit<CartLine, "qty">, qty = 1): void {
  const lines = [...read()];
  const i = lines.findIndex((l) => l.productId === line.productId && l.variantId === line.variantId);
  if (i >= 0) lines[i] = { ...lines[i], qty: clampQty(lines[i].qty + qty) };
  else lines.push({ ...line, qty: clampQty(qty) });
  write(lines);
}

export function setQty(productId: string, variantId: number, qty: number): void {
  if (qty <= 0) return removeFromCart(productId, variantId);
  write(read().map((l) =>
    l.productId === productId && l.variantId === variantId ? { ...l, qty: clampQty(qty) } : l,
  ));
}

export function removeFromCart(productId: string, variantId: number): void {
  write(read().filter((l) => !(l.productId === productId && l.variantId === variantId)));
}

export function clearCart(): void {
  write([]);
}

export function cartCount(lines: CartLine[]): number {
  return lines.reduce((n, l) => n + l.qty, 0);
}

export function cartSubtotal(lines: CartLine[]): number {
  return lines.reduce((sum, l) => sum + l.unitPrice * l.qty, 0);
}

export function subscribeCart(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
